import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { CognitoAuthService } from "../../services/cognitoService";
import AuthLayout, {
  AuthError,
  authButtonClassName,
  authInputClassName,
  authLabelClassName,
  authIconButtonClassName,
  authLinkButtonClassName,
} from "../../components/auth/AuthLayout";

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await CognitoAuthService.forgotPassword(email);
      setIsCodeSent(true);
      setSuccess("Verification code sent to your email.");
    } catch (err: any) {
      setError(err.message || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      await CognitoAuthService.resetPassword(
        email,
        verificationCode,
        newPassword
      );
      setSuccess("Password reset successfully!");

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err: any) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={isCodeSent ? "Reset your password" : "Forgot your password?"}
      subtitle={
        isCodeSent
          ? "Enter the verification code sent to your email"
          : "Enter your email and we'll send you a code to reset your password."
      }
    >
      <div className="space-y-5">
        {error && <AuthError message={error} />}

        {success && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="text-sm text-green-800">{success}</div>
          </div>
        )}

        {!isCodeSent ? (
          <form className="w-full space-y-5" onSubmit={handleSendCode}>
            <div>
              <label htmlFor="email" className={authLabelClassName}>
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={authInputClassName}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={authButtonClassName}
            >
              {loading ? "Sending..." : "Send reset code"}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className={authLinkButtonClassName}
              >
                Back to sign in
              </button>
            </div>
          </form>
        ) : (
          <form className="w-full space-y-5" onSubmit={handleResetPassword}>
            <div>
              <label htmlFor="code" className={authLabelClassName}>
                Verification code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                required
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className={authInputClassName}
              />
            </div>

            <div>
              <label htmlFor="newPassword" className={authLabelClassName}>
                New password
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`${authInputClassName} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className={authIconButtonClassName}
                  aria-label={
                    showNewPassword ? "Hide password" : "Show password"
                  }
                >
                  {showNewPassword ? (
                    <FaEyeSlash className="h-4 w-4" />
                  ) : (
                    <FaEye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className={authLabelClassName}>
                Confirm password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`${authInputClassName} pr-12`}
                />
                <button
                  type="button"
                  onClick={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  className={authIconButtonClassName}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash className="h-4 w-4" />
                  ) : (
                    <FaEye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={authButtonClassName}
            >
              {loading ? "Resetting..." : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
